<script setup lang="ts">
    import EchoButton from '../components/EchoButton.vue';
    import Grid from '../components/grid/Grid.vue';
    import PageHeader from '../components/PageHeader.vue';
    import IconButton from '../components/IconButton.vue';
    import { __ } from '../stores/expressions';
    import DashboardNavigation from '../components/dashboard/DashboardNavigation.vue';
    import ShareDashboardModal from '../components/dashboard/ShareDashboardModal.vue';
    import { useDashboardStore } from '../stores/dashboard';
    import { computed, nextTick, ref, watch } from 'vue';
    import { IDashboard, RequestPostDashboard } from '../services/api/dashboard/types';
    import { useDashboardRouteSync } from '../composables/useDashboardRouteSync';
    import AddWidgetFromModal from '../components/dashboard/AddWidgetFromModal.vue';
    import CreateWidgetModal from '@/js/components/widgets/CreateWidgetModal.vue';
    import { useRouter } from 'vue-router';
    import LoadingDots from '../components/LoadingDots.vue';
    import { useConfirmationDialog } from '../stores/confirmationDialog';
    import DashboardMenu from '../components/dashboard/DashboardMenu.vue';
    import PlaceholderWidget from '../components/widgets/PlaceholderWidget.vue';
    import LoadingOverlay from '../components/LoadingOverlay.vue';
    import { isProd } from '@/config';
    import SvgIcon from '../components/SvgIcon.vue';
    import UserAvatar from '../components/UserAvatar.vue';
    import { useQuery } from '@tanstack/vue-query';
    import { QK } from '../consts/query-keys';
    import { getUserSharesForDashboard } from '../services/api/shareDashboard/shareDashboardApi';
    import { usePermissions } from '../composables/usePermissions';
    import { PermissionArea, DashboardModulePermissionFlag } from '../consts/permissions';
    import {
        CheckIcon,
        LayoutGridIcon,
        PencilIcon,
        PlusIcon,
        Share2Icon,
        StarIcon,
        TrashIcon,
        UsersIcon,
        XIcon,
    } from '@lucide/vue';
    import DashboardNavigationDropdown from '../components/dashboard/DashboardNavigationDropdown.vue';
    import TextInput, { TextInputWidth } from '../components/TextInput.vue';

    const { hasPermission } = usePermissions();

    const canWrite = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ManageDashboards
    );
    const canShareUrl = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareUrl
    );
    const canShareWithUsers = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithUsers
    );
    const canShareWithRoles = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithRoles
    );

    const dashboard = useDashboardStore();
    const dashboardRouteSync = useDashboardRouteSync(dashboard);

    const selectedDashboard = computed(() => dashboard.selectedDashboard);

    const isDashboardOwner = computed(() => dashboard.isDashboardOwner);

    const MAX_VISIBLE_AVATARS = 3;

    const canWriteDashboard = computed(() => isDashboardOwner.value && canWrite.value);

    const canShareDashboard = computed(
        () =>
            isDashboardOwner.value &&
            (canShareUrl.value || canShareWithUsers.value || canShareWithRoles.value)
    );

    const { data: sharedUsersData } = useQuery({
        queryKey: computed(() => [QK.SHARE_DASHBOARD.USERS, selectedDashboard.value?.id]),
        queryFn: () => getUserSharesForDashboard(selectedDashboard.value!.id),
        enabled: computed(() => canShareWithUsers.value && !!selectedDashboard.value?.id),
    });

    const sharedUsers = computed(() => sharedUsersData.value ?? []);
    const visibleUsers = computed(() => sharedUsers.value.slice(0, MAX_VISIBLE_AVATARS));
    const overflowCount = computed(() =>
        Math.max(0, sharedUsers.value.length - MAX_VISIBLE_AVATARS)
    );

    const show_share = ref<boolean>(false);
    const shareInitialView = ref<'users' | 'roles' | 'links' | 'all-shares'>('users');
    const show_add_widget_from = ref<boolean>(false);
    const changeTitleMode = ref(false);
    const dashboardTitle = ref('');
    const titleInput = ref<InstanceType<typeof TextInput> | null>(null);

    const router = useRouter();

    const confirm = useConfirmationDialog();

    const isCopyingDashboard = ref(false);

    const copyDashboard = async () => {
        if (!selectedDashboard.value) return;
        const newTitle = `${selectedDashboard.value.title} (${__('dashboard-copy')})`;
        isCopyingDashboard.value = true;
        try {
            await dashboard.copyItem(selectedDashboard.value.id, newTitle);
        } finally {
            isCopyingDashboard.value = false;
        }
    };

    const toggleFavorite = async () => {
        if (!selectedDashboard.value) return;
        try {
            await dashboard.toggleFavorite(selectedDashboard.value.id);
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };

    const createNewDashboard = () => {
        if (!canWrite.value) return;
        clearTitleChange();
        const newDashboard: RequestPostDashboard = {
            title: __('new-dashboard'),
        };
        dashboard.addItem(newDashboard);
    };

    const updateTitle = (dashboardId: string) => {
        if (!canWriteDashboard.value) return;
        dashboard.updateItemTitle(dashboardId, dashboardTitle.value);
        changeTitleMode.value = false;
    };

    const clearTitleChange = () => {
        dashboardTitle.value = '';
        changeTitleMode.value = false;
    };

    const changeEditMode = async () => {
        if (!canWriteDashboard.value) return;
        changeTitleMode.value = true;
        dashboardTitle.value = selectedDashboard.value?.title ?? '';
        await nextTick();
        titleInput.value?.focus();
    };

    const handleAddNewWidget = (from: 'library' | 'new' | 'ask-me') => {
        if (!canWriteDashboard.value) return;
        show_add_widget_from.value = false;
        if (from === 'library') {
            router.push('library');
        } else if (from === 'new') {
            dashboardRouteSync.openWidgetForm('new');
        } else if (from === 'ask-me') {
            router.push('../ask-me');
        }
    };

    const isEmptyDashboard = (selectedDashboard: IDashboard) =>
        !dashboard.isLoadingDashboard && selectedDashboard.widgets.length === 0;

    const handleEditWidget = (widgetId: string) => {
        if (!canWriteDashboard.value) return;
        dashboardRouteSync.openWidgetForm(widgetId);
    };
    const handleDeleteDashboard = async () => {
        if (!canWriteDashboard.value || !dashboard.selectedDashboardId) {
            return;
        }
        confirm
            .openDialog({
                title: __('dashboard-confirm-delete-dashboard-title', {
                    title: selectedDashboard?.value?.title ?? '',
                }),
                message: __('dashboard-confirm-delete-dashboard-description', {
                    title: selectedDashboard?.value?.title ?? '',
                }),
                confirmText: __('dashboard-confirm-delete-dashboard-button'),
            })
            .then(async (confirmed: boolean) => {
                if (confirmed) {
                    await dashboard.deleteItem(dashboard.selectedDashboardId!);
                }
            });
    };

    const handleDeleteWidget = async (widgetId: string, widgetName: string) => {
        if (!canWriteDashboard.value) return;
        confirm
            .openDialog({
                title: __('dashboard-confirm-delete-widget-title', {
                    title: widgetName ?? 'widget',
                }),
                message: __('dashboard-confirm-delete-widget-description', {
                    title: widgetName ?? 'widget',
                }),
                confirmText: __('dashboard-confirm-delete-widget-button'),
            })
            .then(async (confirmed: boolean) => {
                if (confirmed) {
                    await dashboard.removeWidgetFromDashboard(widgetId);
                }
            });
    };

    const handleUpdateComment = (widgetId: string, value: string) => {
        if (!canWriteDashboard.value) return;
        dashboard.updateWidgetComment(widgetId, value);
    };
    watch([show_share, show_add_widget_from], () => {
        if (show_share.value || show_add_widget_from.value) {
            clearTitleChange();
        }
    });

    const openShareAllShares = () => {
        if (!canShareDashboard.value) return;
        shareInitialView.value = 'all-shares';
        show_share.value = true;
    };

    const openShare = () => {
        if (!canShareDashboard.value) return;
        if (canShareWithUsers.value) {
            shareInitialView.value = 'users';
        } else if (canShareWithRoles.value) {
            shareInitialView.value = 'roles';
        } else {
            shareInitialView.value = 'all-shares';
        }
        show_share.value = true;
    };

    const hasDashboards = computed(() => dashboard.items.length > 0);

    const noDashboards = computed(() => !dashboard.isLoadingItems && dashboard.items.length === 0);

    const dashboardLoadingError = computed(() => {
        if (dashboard.selectedDashboardId && !dashboard.isLoadingDashboard) {
            return dashboard.selectedDashboard === undefined;
        }

        return false;
    });
</script>

<template>
    <div class="page-padding flex flex-col min-h-screen">
        <PageHeader>
            <template #icon><LayoutGridIcon class="size-full" /></template>
            {{ __('dashboard') }}

            <template #links>
                <DashboardNavigationDropdown />

                <EchoButton v-if="canWrite" @click="createNewDashboard()">
                    {{ __('new-dashboard') }}
                    <template #icon-right><PlusIcon /></template>
                </EchoButton>
            </template>
        </PageHeader>
        <AddWidgetFromModal
            :show="show_add_widget_from"
            @add-widget="handleAddNewWidget"
            @close="show_add_widget_from = false"
        />
        <CreateWidgetModal
            :show="canWriteDashboard && !!dashboard.widgetFormOpen"
            @close="dashboardRouteSync.closeWidgetForm"
        />
        <ShareDashboardModal
            :show="show_share && (canShareUrl || canShareWithUsers || canShareWithRoles)"
            :initial-view="shareInitialView"
            @close="show_share = false"
        />
        <div class="mt-1 relative bg-white rounded-lg shadow-md p-6 flex flex-col grow">
            <DashboardNavigation v-if="hasDashboards" />

            <LoadingOverlay
                :show="
                    dashboard.isRefetching ||
                    isCopyingDashboard ||
                    (dashboard.isLoadingDashboard && !!selectedDashboard)
                "
            />
            <LoadingDots
                v-if="dashboard.isLoadingDashboard && !selectedDashboard"
                class="h-96"
                large
            />

            <div v-if="noDashboards" class="flex flex-col items-center justify-center h-96">
                <SvgIcon
                    name="sentiment-neutraltonegative-gray.svg"
                    class="stroke-transparent"
                    size="size-20"
                />
                <p class="text-sm font-semibold mt-4">
                    {{ __('dashboard-empty-message') }}
                </p>
                <EchoButton v-if="canWrite" variant="tertiary" @click="createNewDashboard">
                    {{ __('create-first-dashboard') }}
                    <template #icon-right><PlusIcon /></template>
                </EchoButton>
            </div>
            <div v-else-if="!dashboard.isLoadingDashboard && dashboard.selectedDashboardId">
                <div
                    v-if="!dashboardLoadingError"
                    class="flex justify-between pb-4 flex-col items-start gap-3 md:flex-row md:items-center md:gap:0"
                >
                    <div v-if="!changeTitleMode" class="flex gap-0.5 items-center">
                        <h3 class="heading-lg-light pr-2">
                            {{ selectedDashboard?.title ?? '' }}
                        </h3>
                        <IconButton
                            v-if="canWriteDashboard"
                            area-size="size-8"
                            :tooltip="__('dashboard-edit-title')"
                            @click="changeEditMode"
                        >
                            <PencilIcon class="size-full" />
                        </IconButton>
                        <IconButton
                            area-size="size-8"
                            :tooltip="
                                selectedDashboard?.isFavorite
                                    ? __('unfavorite-dashboard')
                                    : __('favorite-dashboard')
                            "
                            :aria-pressed="selectedDashboard?.isFavorite"
                            :class="selectedDashboard?.isFavorite ? 'text-yellow' : ''"
                            @click="toggleFavorite"
                        >
                            <StarIcon
                                :fill="selectedDashboard?.isFavorite ? 'currentColor' : 'none'"
                            />
                        </IconButton>
                        <DashboardMenu
                            class="mr-6"
                            :show-delete="canWriteDashboard"
                            @add-widget="show_add_widget_from = true"
                            @copy-dashboard="copyDashboard"
                            @delete-dashboard="handleDeleteDashboard"
                        />
                        <EchoButton
                            v-if="canWriteDashboard"
                            variant="primary"
                            @click="
                                () => {
                                    if (isProd) {
                                        dashboardRouteSync.openWidgetForm('new');
                                    } else {
                                        show_add_widget_from = true;
                                    }
                                }
                            "
                        >
                            {{ __('dashboard-add-widget') }}
                            <template #icon-right><PlusIcon /></template>
                        </EchoButton>
                    </div>
                    <div v-else class="flex justify-between items-center gap-3">
                        <TextInput
                            ref="titleInput"
                            v-model="dashboardTitle"
                            :width="TextInputWidth.Medium"
                            :aria-label="__('change-dashboard-title')"
                        ></TextInput>
                        <div class="flex gap-1.5">
                            <EchoButton
                                variant="primary"
                                @click="updateTitle(dashboard.selectedDashboardId!)"
                            >
                                {{ __('save') }}
                                <template #icon-right><CheckIcon /></template>
                            </EchoButton>
                            <EchoButton @click="clearTitleChange">
                                {{ __('cancel') }}
                                <template #icon-right><XIcon class="size-full" /></template>
                            </EchoButton>
                        </div>
                    </div>
                    <div v-if="canShareDashboard" class="flex gap-3 items-center pr-0.5">
                        <div v-if="sharedUsers.length > 0" class="flex items-center">
                            <div class="flex -space-x-2">
                                <button
                                    v-for="(user, index) in visibleUsers"
                                    :key="user.id"
                                    type="button"
                                    class="cursor-pointer custom-focus rounded-full"
                                    :aria-label="user.name || user.email"
                                    :style="{ zIndex: 10 - index }"
                                    @click="openShareAllShares"
                                >
                                    <UserAvatar :label="user.name || user.email" color="plum" />
                                </button>
                                <button
                                    v-if="overflowCount > 0"
                                    type="button"
                                    class="w-9 h-9 rounded-full bg-gray-lightest text-caption-bold flex items-center justify-center cursor-pointer"
                                    :aria-label="__('show-all-shares')"
                                    @click="openShareAllShares"
                                >
                                    +{{ overflowCount }}
                                </button>
                            </div>
                            <IconButton
                                class="Icon-button md:size-8 ml-1"
                                area-size="size-6"
                                :tooltip="__('show-all-shares')"
                                @click="openShareAllShares"
                            >
                                <UsersIcon class="size-full" />
                            </IconButton>
                        </div>
                        <EchoButton variant="tertiary" @click="openShare">
                            {{ __('share') }}
                            <template #icon-right><Share2Icon /></template>
                        </EchoButton>
                    </div>
                    <div v-else-if="!isDashboardOwner" class="flex gap-2 items-center pr-0.5">
                        <span class="heading-sm">{{ __('shared-with-me') }}</span>
                        <UsersIcon class="mb-1 size-7" />
                    </div>
                </div>
                <div
                    v-else-if="dashboardLoadingError"
                    class="flex flex-col items-center justify-center h-96"
                >
                    <SvgIcon
                        name="sentiment-neutraltonegative-gray.svg"
                        class="stroke-transparent"
                        size="size-20"
                    />

                    <p class="text-sm font-semibold mt-4 mb-4">
                        {{ __('dashboard-broken') }}
                    </p>

                    <p>
                        <EchoButton
                            v-if="canWriteDashboard"
                            variant="primary"
                            @click="handleDeleteDashboard"
                        >
                            {{ __('dashboard-delete-dashboard') }}
                            <template #icon-right><TrashIcon /></template>
                        </EchoButton>
                    </p>
                </div>

                <Grid
                    v-if="selectedDashboard"
                    :dashboard-id="selectedDashboard.id"
                    :widget-data="selectedDashboard.widgets"
                    :widget-layout="selectedDashboard.widgetLayout"
                    :static-grid="!canWriteDashboard"
                    @edit-widget="handleEditWidget"
                    @delete-widget="handleDeleteWidget"
                    @update-comment="handleUpdateComment"
                />
                <PlaceholderWidget
                    v-if="
                        canWriteDashboard &&
                        selectedDashboard &&
                        !isEmptyDashboard(selectedDashboard)
                    "
                    :is-new-dashboard="false"
                    @open-form="handleEditWidget"
                />
            </div>
        </div>
    </div>
</template>
